// AirX custom reviews — modal submission + reviews display + pagination.
// Backend: Google Apps Script (URL in section's data-api-url attribute).
(() => {
  if (window.__airxReviewsInit) return;
  window.__airxReviewsInit = true;

  const PAGE_SIZE = 10;
  const MAX_FILES = 6;
  const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.from(root.querySelectorAll(sel)); }

  // Star SVG renderer for review cards
  function renderStars(rating, color) {
    const filled = Math.round(rating);
    let html = '<span style="display: inline-block; letter-spacing: 1px; font-size: 1rem; font-family: Arial, sans-serif;">';
    for (let i = 1; i <= 5; i++) {
      html += `<span style="color: ${i <= filled ? color : 'rgba(0,0,0,0.18)'};">★</span>`;
    }
    html += '</span>';
    return html;
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Result like "data:image/png;base64,iVBORw0K..."
        const comma = reader.result.indexOf(',');
        resolve(comma >= 0 ? reader.result.slice(comma + 1) : reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Cache product image URLs per handle to avoid repeated fetches.
  const productImageCache = new Map();
  async function fetchProductImage(handle) {
    if (!handle) return null;
    if (productImageCache.has(handle)) return productImageCache.get(handle);
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.json');
      if (!res.ok) throw new Error('http ' + res.status);
      const data = await res.json();
      const src = (data && data.product && data.product.image && data.product.image.src) || null;
      productImageCache.set(handle, src);
      return src;
    } catch (e) {
      productImageCache.set(handle, null);
      return null;
    }
  }

  function initSection(root) {
    const apiUrl = root.dataset.apiUrl;
    const productHandle = root.dataset.productHandle || '';
    const productTitle = root.dataset.productTitle || '';
    const itemType = root.dataset.itemType || '';
    const starColor = root.dataset.starColor || '#f5a524';

    if (!apiUrl) {
      console.warn('[airx-reviews] No API URL configured');
      return;
    }

    const summaryText = $(root, '[data-airx-summary-text]');
    const summaryFill = $(root, '[data-airx-stars-fill]');
    const listEl = $(root, '[data-airx-reviews-list]');
    const emptyEl = $(root, '[data-airx-empty]');
    const showMoreBtn = $(root, '[data-airx-show-more]');
    const openModalBtn = $(root, '[data-airx-open-modal]');

    let offset = 0;
    let total = 0;
    let isLoading = false;
    const originalShowMoreText = showMoreBtn?.textContent || '';

    // ─── Render list ────────────────────────────────────────────────────────
    async function loadReviews(append) {
      if (isLoading) return;
      isLoading = true;
      if (append && showMoreBtn) {
        showMoreBtn.disabled = true;
        showMoreBtn.textContent = 'Loading…';
        showMoreBtn.style.opacity = '0.6';
        showMoreBtn.style.cursor = 'wait';
        showMoreBtn.style.pointerEvents = 'none';
      }
      try {
        const url = `${apiUrl}?action=list&offset=${offset}&limit=${PAGE_SIZE}`;
        const res = await fetch(url);
        const data = await res.json();
        if (data._http) throw new Error(data.error || 'API error');
        total = data.total || 0;
        const reviews = data.reviews || [];

        if (!append) listEl.innerHTML = '';
        emptyEl?.remove();

        if (reviews.length === 0 && !append) {
          listEl.innerHTML = '<div style="text-align: center; padding: 32px 16px; opacity: 0.7;">No reviews yet. Be the first!</div>';
          showMoreBtn.hidden = true;
          return;
        }

        // Switch to 2-col on desktop after first batch
        listEl.style.gridTemplateColumns = window.matchMedia('(min-width: 720px)').matches ? '1fr 1fr' : '1fr';

        reviews.forEach(r => listEl.appendChild(renderCard(r)));
        offset += reviews.length;
        showMoreBtn.hidden = offset >= total;
      } catch (err) {
        console.error('[airx-reviews] list error', err);
        if (!append) listEl.innerHTML = '<div style="text-align: center; padding: 32px 16px; opacity: 0.7;">Could not load reviews.</div>';
      } finally {
        isLoading = false;
        if (showMoreBtn) {
          showMoreBtn.disabled = false;
          showMoreBtn.textContent = originalShowMoreText;
          showMoreBtn.style.opacity = '';
          showMoreBtn.style.cursor = '';
          showMoreBtn.style.pointerEvents = '';
        }
      }
    }

    const reviewsById = new Map();
    function renderCard(r) {
      reviewsById.set(r.id, r);
      const card = document.createElement('article');
      card.style.cssText = 'border: 1px solid rgba(0,0,0,0.1); border-radius: 8px; padding: 16px; background: #fff; color: #111; cursor: pointer; transition: box-shadow 0.15s, border-color 0.15s;';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.dataset.reviewId = r.id;
      const name = `${escapeHtml(r.first_name)}${r.last_initial ? ' ' + r.last_initial + '.' : ''}`;
      const date = r.timestamp ? new Date(r.timestamp).toLocaleDateString() : '';
      const mediaHtml = renderMedia(r.id, r.photo_urls, r.video_urls);
      card.innerHTML = `
        <div style="display: flex; align-items: center; gap: 6px; font-weight: 700;">
          ${escapeHtml(name)}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#22c55e" aria-label="Verified"><circle cx="12" cy="12" r="12"/><path d="M7 12.5l3 3 7-7" stroke="#fff" stroke-width="2.5" fill="none"/></svg>
        </div>
        <div style="font-size: 0.8rem; opacity: 0.6; margin: 2px 0 8px;">${escapeHtml(date)}</div>
        ${renderStars(r.rating, starColor)}
        ${r.title ? `<div style="font-weight: 700; margin: 10px 0 4px;">${escapeHtml(r.title)}</div>` : ''}
        <p style="margin: 8px 0 0; line-height: 1.45; white-space: pre-wrap;">${escapeHtml(r.comment)}</p>
        ${r.item_type ? `<div style="font-size: 0.85rem; margin-top: 12px; padding-top: 10px; border-top: 1px solid rgba(0,0,0,0.08);"><span style="opacity: 0.6;">Item type:</span><br><span>${escapeHtml(r.item_type)}</span></div>` : ''}
        ${r.product_title || r.product_handle ? `
          <div style="font-size: 0.85rem; margin-top: 10px; display: flex; align-items: center; gap: 10px;">
            <img data-product-thumb-handle="${escapeHtml(r.product_handle || '')}" alt="" loading="lazy" referrerpolicy="no-referrer" width="44" height="44" style="width: 44px; height: 44px; object-fit: cover; border-radius: 4px; display: none; flex-shrink: 0; border: 1px solid rgba(0,0,0,0.08);">
            <span style="font-weight: 500;">${escapeHtml(r.product_title || '')}</span>
          </div>
        ` : ''}
        ${mediaHtml}
      `;
      // Lazy-load the product thumbnail (uses cache)
      const thumb = card.querySelector('[data-product-thumb-handle]');
      if (thumb && thumb.dataset.productThumbHandle) {
        fetchProductImage(thumb.dataset.productThumbHandle).then(src => {
          if (src) {
            thumb.src = src;
            thumb.style.display = '';
          }
        });
      }
      card.addEventListener('mouseenter', () => { card.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; card.style.borderColor = 'rgba(0,0,0,0.2)'; });
      card.addEventListener('mouseleave', () => { card.style.boxShadow = ''; card.style.borderColor = 'rgba(0,0,0,0.1)'; });
      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const firstPhoto = (r.photo_urls && r.photo_urls[0]) ? imageDisplayUrl(r.photo_urls[0]) : '';
          openDetail(r.id, firstPhoto);
        }
      });
      return card;
    }

    // ─── Detail modal (click a photo) ───────────────────────────────────────
    const detailModal = document.querySelector('[data-airx-detail-modal]');
    function openDetail(reviewId, photoUrl) {
      if (!detailModal) return;
      const r = reviewsById.get(reviewId);
      if (!r) return;
      const photoWrap = detailModal.querySelector('[data-airx-detail-photo-wrap]');
      const img = detailModal.querySelector('[data-airx-detail-img]');
      if (photoUrl) {
        img.src = photoUrl;
        photoWrap.style.display = '';
      } else {
        img.src = '';
        photoWrap.style.display = 'none';
      }
      detailModal.querySelector('[data-airx-detail-name]').textContent =
        `${r.first_name || ''}${r.last_initial ? ' ' + r.last_initial + '.' : ''}`;
      detailModal.querySelector('[data-airx-detail-date]').textContent =
        r.timestamp ? new Date(r.timestamp).toLocaleDateString() : '';
      detailModal.querySelector('[data-airx-detail-rating]').innerHTML =
        renderStars(r.rating, starColor);
      detailModal.querySelector('[data-airx-detail-comment]').textContent = r.comment || '';
      const itTypeWrap = detailModal.querySelector('[data-airx-detail-item-type]');
      if (r.item_type) {
        detailModal.querySelector('[data-airx-detail-item-type-val]').textContent = r.item_type;
        itTypeWrap.hidden = false;
      } else { itTypeWrap.hidden = true; }
      const prodEl = detailModal.querySelector('[data-airx-detail-product]');
      const prodTitleEl = detailModal.querySelector('[data-airx-detail-product-title]');
      const prodLinkEl = detailModal.querySelector('[data-airx-detail-product-link]');
      const prodImgEl = detailModal.querySelector('[data-airx-detail-product-img]');
      if (r.product_title || r.product_handle) {
        if (prodTitleEl) prodTitleEl.textContent = r.product_title || '';
        if (prodImgEl) prodImgEl.style.display = 'none';
        if (prodLinkEl) {
          if (r.product_handle) {
            prodLinkEl.href = '/products/' + encodeURIComponent(r.product_handle);
            prodLinkEl.style.display = '';
            fetchProductImage(r.product_handle).then(src => {
              if (src && prodImgEl) {
                prodImgEl.src = src;
                prodImgEl.style.display = '';
              }
            });
          } else {
            prodLinkEl.style.display = 'none';
          }
        }
        prodEl.hidden = false;
      } else { prodEl.hidden = true; }
      detailModal.hidden = false;
      document.body.style.overflow = 'hidden';
    }
    function closeDetail() {
      if (!detailModal) return;
      detailModal.hidden = true;
      document.body.style.overflow = '';
    }
    if (detailModal) {
      detailModal.querySelector('[data-airx-detail-close]')?.addEventListener('click', closeDetail);
      detailModal.addEventListener('click', e => { if (e.target === detailModal) closeDetail(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && !detailModal.hidden) closeDetail(); });
    }
    // Event delegation: photo button takes its own photo URL; clicks anywhere
    // else on the card open the modal with the first photo (or no photo).
    listEl.addEventListener('click', e => {
      const photoBtn = e.target.closest('[data-airx-review-photo]');
      if (photoBtn) {
        e.preventDefault();
        openDetail(photoBtn.dataset.reviewId, photoBtn.dataset.photoUrl);
        return;
      }
      const cardEl = e.target.closest('[data-review-id]');
      if (cardEl) {
        const r = reviewsById.get(cardEl.dataset.reviewId);
        const firstPhoto = (r && r.photo_urls && r.photo_urls[0]) ? imageDisplayUrl(r.photo_urls[0]) : '';
        openDetail(cardEl.dataset.reviewId, firstPhoto);
      }
    });

    // Extract a Drive file ID from any of the URL formats we might have stored.
    function driveFileIdFrom(url) {
      if (!url) return null;
      let m = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (m) return m[1];
      m = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (m) return m[1];
      m = url.match(/lh3\.googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/);
      if (m) return m[1];
      return null;
    }
    function imageDisplayUrl(url) {
      const id = driveFileIdFrom(url);
      return id ? `https://lh3.googleusercontent.com/d/${id}=w1600` : url;
    }
    function videoDisplayUrl(url) {
      const id = driveFileIdFrom(url);
      return id ? `https://drive.google.com/file/d/${id}/preview` : url;
    }

    function renderMedia(reviewId, photos, videos) {
      const all = [
        ...(photos || []).map(u => ({ url: u, type: 'image' })),
        ...(videos || []).map(u => ({ url: u, type: 'video' }))
      ];
      if (all.length === 0) return '';
      const items = all.map(m => {
        if (m.type === 'video') {
          const href = videoDisplayUrl(m.url);
          return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener" style="display: inline-block; background: #000; color: #fff; padding: 8px 14px; border-radius: 6px; font-size: 0.85rem; text-decoration: none;">▶ Video</a>`;
        }
        const src = imageDisplayUrl(m.url);
        return `<button type="button" data-airx-review-photo data-review-id="${escapeHtml(reviewId)}" data-photo-url="${escapeHtml(src)}" style="border: 0; background: transparent; padding: 0; cursor: zoom-in;"><img src="${escapeHtml(src)}" alt="" loading="lazy" referrerpolicy="no-referrer" style="width: 64px; height: 64px; object-fit: cover; border-radius: 6px; border: 1px solid rgba(0,0,0,0.1); display: block;" onerror="this.style.display='none'"></button>`;
      }).join('');
      return `<div style="margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px;">${items}</div>`;
    }

    const STATS_CACHE_KEY = 'airx-reviews-stats';
    const STATS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

    function applyStats(data) {
      const count = data.count || 0;
      const avg = data.average || 0;
      if (summaryFill) summaryFill.style.width = (avg * 20) + '%';
      if (summaryText) summaryText.textContent = count === 0
        ? 'No reviews yet'
        : `${avg.toFixed(1)} / 5 · ${count.toLocaleString()} review${count === 1 ? '' : 's'}`;
      // Broadcast stats so other elements (rating block at top of page) can listen
      document.dispatchEvent(new CustomEvent('airx:reviews:stats', { detail: { count, avg } }));
    }

    async function loadStats() {
      // Apply cached stats immediately for an instant render. Stats are
      // store-wide so the same cache is valid across every product page.
      try {
        const cached = localStorage.getItem(STATS_CACHE_KEY);
        if (cached) {
          const d = JSON.parse(cached);
          if (d && Date.now() - (d._t || 0) < STATS_CACHE_TTL_MS) {
            applyStats(d);
          }
        }
      } catch (_) {}
      // Then fetch fresh in background — keeps numbers accurate when reviews
      // are approved or new ones come in.
      try {
        const res = await fetch(`${apiUrl}?action=stats`);
        const data = await res.json();
        applyStats(data);
        try {
          localStorage.setItem(STATS_CACHE_KEY, JSON.stringify({
            count: data.count || 0,
            average: data.average || 0,
            _t: Date.now()
          }));
        } catch (_) {}
      } catch (err) {
        console.error('[airx-reviews] stats error', err);
      }
    }

    showMoreBtn?.addEventListener('click', () => loadReviews(true));

    // Load stats AND the reviews list immediately so the section is fully
    // sized by the time the user clicks the rating block at the top.
    // Lazy-loading caused a "first click scrolls to middle, second click
    // scrolls correctly" bug because the section grew after the scroll.
    loadStats();
    loadReviews(false);

    // ─── Live polling for moderation updates ────────────────────────────────
    // Every 60s while the tab is visible, ask the backend for fresh stats.
    // If the approved count changed (owner approved/rejected a review),
    // re-fetch the list so new reviews appear without a manual page reload.
    let lastKnownCount = null;
    async function pollUpdates() {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await fetch(`${apiUrl}?action=stats`);
        const data = await res.json();
        const count = data.count || 0;
        const avg = data.average || 0;
        if (summaryFill) summaryFill.style.width = (avg * 20) + '%';
        if (summaryText) summaryText.textContent = count === 0
          ? 'No reviews yet'
          : `${avg.toFixed(1)} / 5 · ${count.toLocaleString()} review${count === 1 ? '' : 's'}`;
        document.dispatchEvent(new CustomEvent('airx:reviews:stats', { detail: { count, avg } }));
        if (lastKnownCount !== null && count !== lastKnownCount) {
          // Approval state changed — reload the list from the top.
          offset = 0;
          await loadReviews(false);
        }
        lastKnownCount = count;
      } catch (_) { /* ignore — try again next tick */ }
    }
    // Seed lastKnownCount after the initial stats land, then poll on a timer.
    document.addEventListener('airx:reviews:stats', e => {
      if (lastKnownCount === null) lastKnownCount = (e.detail && e.detail.count) || 0;
    }, { once: true });
    setInterval(pollUpdates, 60000);
    // Also poll once when the tab becomes visible (after being hidden), so the
    // user sees fresh content immediately on return.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') pollUpdates();
    });

    // ─── Modal ──────────────────────────────────────────────────────────────
    const modal = document.querySelector('[data-airx-modal]');
    if (!modal) return;

    let state = { step: 1, rating: 0, media: [], title: '', comment: '', firstName: '', lastName: '', email: '' };

    const stepEls = $$(modal, '[data-airx-step]');
    const pipEls = $$(modal, '[data-airx-pip]');
    const backBtn = $(modal, '[data-airx-back]');
    const nextBtn = $(modal, '[data-airx-next]');
    const footer = $(modal, '[data-airx-footer]');
    const ratingBtns = $$(modal, '[data-rating]');
    const ratingIcons = $$(modal, '[data-airx-rating-icon]');
    const mediaInput = $(modal, '[data-airx-media-input]');
    const mediaPreviews = $(modal, '[data-airx-media-previews]');
    const mediaError = $(modal, '[data-airx-media-error]');
    const commentInput = $(modal, '[data-airx-comment]');
    const commentError = $(modal, '[data-airx-comment-error]');
    const firstNameInput = $(modal, '[data-airx-first-name]');
    const lastNameInput = $(modal, '[data-airx-last-name]');
    const emailInput = $(modal, '[data-airx-email]');
    const submitError = $(modal, '[data-airx-submit-error]');

    function openModal() {
      state = { step: 1, rating: 0, media: [], title: '', comment: '', firstName: '', lastName: '', email: '' };
      commentInput.value = '';
      firstNameInput.value = '';
      lastNameInput.value = '';
      emailInput.value = '';
      mediaPreviews.innerHTML = '';
      mediaInput.value = '';
      ratingIcons.forEach(i => i.classList.remove('is-active'));
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      renderStep();
    }

    function closeModal() {
      modal.hidden = true;
      document.body.style.overflow = '';
    }

    function renderStep() {
      stepEls.forEach(el => { el.hidden = parseInt(el.dataset.airxStep, 10) !== state.step; });
      pipEls.forEach(p => {
        const i = parseInt(p.dataset.airxPip, 10);
        p.style.background = i <= state.step ? '#111' : '#ddd';
      });
      backBtn.hidden = state.step <= 1 || state.step === 5;
      nextBtn.hidden = state.step === 5;
      nextBtn.textContent = state.step === 4 ? 'Done' : 'Next';
      footer.style.display = state.step === 5 ? 'none' : 'flex';

      // Step 2 copy depends on rating
      if (state.step === 2) {
        const isLow = state.rating > 0 && state.rating <= 2;
        const titleEl = modal.querySelector('[data-airx-step2-title]');
        const subEl   = modal.querySelector('[data-airx-step2-subtitle]');
        const incEl   = modal.querySelector('[data-airx-step2-incentive]');
        if (titleEl) titleEl.textContent = isLow ? "We're sorry to hear that" : 'Show it off';
        if (subEl)   subEl.textContent   = isLow ? 'Show us what went wrong (Optional)' : "We'd love to see it in action! (Optional)";
        if (incEl)   incEl.hidden        = !isLow;
      }
    }

    function setRating(r) {
      state.rating = r;
      ratingIcons.forEach((icon, idx) => {
        icon.classList.toggle('is-active', idx < r);
      });
    }

    async function addMediaFiles(files) {
      mediaError.hidden = true;
      const remaining = MAX_FILES - state.media.length;
      const list = Array.from(files).slice(0, remaining);
      for (const file of list) {
        if (file.size > MAX_FILE_BYTES) {
          mediaError.textContent = `"${file.name}" is too large (max 10 MB).`;
          mediaError.hidden = false;
          continue;
        }
        try {
          const data_base64 = await fileToBase64(file);
          state.media.push({ filename: file.name, mime_type: file.type, data_base64 });
          renderMediaPreviews();
        } catch (err) {
          console.error('[airx-reviews] file read failed', err);
        }
      }
      if (state.media.length >= MAX_FILES) {
        mediaError.textContent = `Maximum ${MAX_FILES} files reached.`;
        mediaError.hidden = false;
      }
    }

    function renderMediaPreviews() {
      mediaPreviews.innerHTML = '';
      state.media.forEach((m, idx) => {
        const wrap = document.createElement('div');
        wrap.style.cssText = 'position: relative; width: 72px; height: 72px;';
        const isVideo = (m.mime_type || '').startsWith('video/');
        const src = `data:${m.mime_type};base64,${m.data_base64}`;
        wrap.innerHTML = isVideo
          ? `<div style="width: 100%; height: 100%; background: #000; color: #fff; display: flex; align-items: center; justify-content: center; border-radius: 6px;">▶</div>`
          : `<img src="${src}" alt="" style="width: 100%; height: 100%; object-fit: cover; border-radius: 6px; border: 1px solid rgba(0,0,0,0.1);">`;
        const x = document.createElement('button');
        x.type = 'button';
        x.textContent = '×';
        x.style.cssText = 'position: absolute; top: -6px; right: -6px; width: 22px; height: 22px; border-radius: 50%; background: #111; color: #fff; border: 0; cursor: pointer; font-size: 14px; line-height: 1;';
        x.addEventListener('click', () => { state.media.splice(idx, 1); renderMediaPreviews(); });
        wrap.appendChild(x);
        mediaPreviews.appendChild(wrap);
      });
    }

    async function submit() {
      submitError.hidden = true;
      const fn = firstNameInput.value.trim();
      const em = emailInput.value.trim();
      if (!fn) { submitError.textContent = 'First name is required.'; submitError.hidden = false; return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { submitError.textContent = 'Valid email is required.'; submitError.hidden = false; return; }

      const payload = {
        rating: state.rating,
        title: state.title || '',
        comment: state.comment,
        first_name: fn,
        last_name: lastNameInput.value.trim(),
        email: em,
        product_handle: productHandle,
        product_title: productTitle,
        item_type: itemType,
        media: state.media
      };

      nextBtn.disabled = true;
      nextBtn.textContent = 'Submitting…';
      try {
        // Apps Script requires text/plain to skip CORS preflight
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.error || data._http) {
          throw new Error(data.details ? data.details.join(', ') : data.error);
        }
        state.step = 5;
        renderStep();
        // Auto-close the thank-you screen after 3 seconds
        setTimeout(() => { if (state.step === 5) closeModal(); }, 3000);
      } catch (err) {
        console.error('[airx-reviews] submit error', err);
        submitError.textContent = 'Submission failed: ' + (err.message || err);
        submitError.hidden = false;
      } finally {
        nextBtn.disabled = false;
        nextBtn.textContent = state.step === 4 ? 'Done' : 'Next';
      }
    }

    function next() {
      if (state.step === 1) {
        if (!state.rating) return;
        state.step = 2; renderStep();
      } else if (state.step === 2) {
        state.step = 3; renderStep();
      } else if (state.step === 3) {
        const c = commentInput.value.trim();
        if (c.length < 3) { commentError.hidden = false; return; }
        commentError.hidden = true;
        state.comment = c;
        state.step = 4; renderStep();
      } else if (state.step === 4) {
        submit();
      }
    }

    function back() {
      if (state.step > 1) { state.step -= 1; renderStep(); }
    }

    openModalBtn?.addEventListener('click', openModal);
    $$(document, '[data-airx-close-modal]').forEach(b => b.addEventListener('click', closeModal));
    modal.addEventListener('click', e => { if (e.target === modal) closeModal(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !modal.hidden) closeModal(); });

    // Enter in the About-you inputs (step 4) triggers Done.
    [firstNameInput, lastNameInput, emailInput].forEach(input => {
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          next();
        }
      });
    });

    ratingBtns.forEach(b => b.addEventListener('click', () => setRating(parseInt(b.dataset.rating, 10))));
    $$(modal, '[data-airx-pick-media]').forEach(b => b.addEventListener('click', () => {
      mediaInput.accept = b.dataset.airxPickMedia === 'video' ? 'video/*' : 'image/*';
      mediaInput.click();
    }));
    mediaInput.addEventListener('change', e => addMediaFiles(e.target.files));
    nextBtn.addEventListener('click', next);
    backBtn.addEventListener('click', back);

    // Expose helper to open the modal from outside (e.g., the rating block at top of page)
    window.AirxReviews = window.AirxReviews || {};
    window.AirxReviews.open = openModal;
    window.AirxReviews.scrollToReviews = () => {
      const doScroll = () => root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      doScroll();
      // Re-scroll a few times to compensate for layout shifts from lazy-
      // loaded sections above (product recommendations images, etc.).
      setTimeout(doScroll, 500);
      setTimeout(doScroll, 1200);
      setTimeout(doScroll, 2200);
    };
  }

  function init() {
    document.querySelectorAll('[data-airx-reviews-root]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
